import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Toast } from "@/ui-kit";
import { addressSchema } from "@/utils/validation";
import { AddressUtils } from "@/utils/address";
import type { IServiceArea } from "@/types/common";
import { serverErrorHandler } from "@/utils/serverErrorHandler";

type IFormData = {
  serviceAreas: IServiceArea[];
};

type IUseServiceAreasFormOptions = {
  userId: number;
  serviceAreas?: IServiceArea[];
  updateServiceArea: (params: {
    userId: number;
    serviceAreas: IServiceArea[];
  }) => Promise<{ serviceAreas: IServiceArea[] }>;
  onSuccess: (serviceAreas: IServiceArea[]) => void;
};

export const useServiceAreasForm = ({
  userId,
  serviceAreas,
  updateServiceArea,
  onSuccess,
}: IUseServiceAreasFormOptions) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const schema = useMemo(() => {
    return z.object({
      serviceAreas: z.array(
        z.object({
          name: z.string().min(1, { message: t("validation.required") }),
          address: addressSchema,
        }),
      ),
    });
  }, [t]);

  const defaultValues = useMemo((): IFormData => {
    return {
      serviceAreas:
        serviceAreas && serviceAreas.length > 0
          ? serviceAreas.map((area) => ({
              name: area.name,
              address: AddressUtils.getNormalized(area.address),
            }))
          : [
              {
                name: "",
                address: AddressUtils.getEmpty(),
              },
            ],
    };
  }, [serviceAreas]);

  const form = useForm<IFormData>({
    defaultValues,
    resolver: zodResolver(schema),
  });

  const { handleSubmit } = form;

  const onSubmit = useCallback(() => {
    handleSubmit((formData: IFormData) => {
      setLoading(true);

      const normalizedAreas = formData.serviceAreas.map((area, i) => ({
        name: area.name || area.address.name || `Area ${i + 1}`,
        address: area.address,
      }));

      updateServiceArea({
        userId,
        serviceAreas: normalizedAreas,
      })
        .then((response) => {
          Toast.success({
            title: t("toast.what_updated", {
              what: t("label.service_area"),
            }),
          });
          onSuccess(response.serviceAreas);
        })
        .catch(serverErrorHandler())
        .finally(() => {
          setLoading(false);
        });
    })();
  }, [handleSubmit, userId, updateServiceArea, onSuccess, t]);

  return {
    form,
    onSubmit,
    loading,
  };
};
